/**
 * Cliente del formulario de contacto de la landing (`POST /api/contact/messages`).
 *
 * En desarrollo la ruta `/api` la redirige el proxy de Vite al backend
 * (ver `vite.config.ts`); en producción el SPA lo sirve el propio API, asi que
 * la misma ruta relativa funciona en ambos entornos.
 */
const CONTACT_ENDPOINT = '/api/contact/messages'

/** Datos que envia el formulario de contacto. */
export interface ContactMessage {
  nombre: string
  email: string
  asunto: string
  mensaje: string
  /**
   * Campo trampa (honeypot): los humanos no lo ven. Si el bot lo diligencia,
   * el backend responde con exito pero no envia ningun correo.
   */
  website?: string
  /**
   * Payload del reto ALTCHA resuelto en el navegador (proof-of-work).
   * Sin el, el backend rechaza el envio (politica fail-closed).
   */
  altcha?: string
}

/** Sobre de respuesta del endpoint. */
export interface ContactResponse {
  success: boolean
  message: string
  statusCode?: number
}

/** Error de negocio con el detalle que devuelve el backend (validacion 400). */
export class ContactFormError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ContactFormError'
    this.status = status
  }
}

/**
 * Envia el mensaje de contacto.
 *
 * @throws ContactFormError cuando el backend responde con error (validacion,
 *         rate limit o servicio no disponible).
 * @throws Error en fallos de red.
 */
export async function sendContactMessage(message: ContactMessage): Promise<ContactResponse> {
  let response: Response

  try {
    response = await fetch(CONTACT_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        name: message.nombre,
        email: message.email,
        subject: message.asunto,
        message: message.mensaje,
        website: message.website ?? '',
        altcha: message.altcha ?? ''
      })
    })
  } catch {
    throw new ContactFormError(
      'No pudimos conectar con el servidor. Revisa tu conexion e intenta de nuevo.',
      0
    )
  }

  const payload = await readJson(response)

  if (!response.ok) {
    throw new ContactFormError(
      payload?.message || `El servidor respondio ${response.status}.`,
      response.status
    )
  }

  return {
    success: payload?.success ?? true,
    message: payload?.message ?? 'Mensaje enviado. Te responderemos pronto.',
    statusCode: payload?.statusCode ?? response.status
  }
}

/** Lee el cuerpo JSON tolerando respuestas vacias o no-JSON. */
async function readJson(response: Response): Promise<ContactResponse | null> {
  try {
    const text = await response.text()
    if (!text) return null
    return JSON.parse(text) as ContactResponse
  } catch {
    return null
  }
}
